import { useState } from "react";
import SiteMenu from "@/components/SiteMenu";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import CelestialBackground from "@/components/CelestialBackground";
import { BirthDatePicker, BirthTimePicker } from "@/components/BirthDateTime";

const inputCls =
  "w-full bg-[#0F1523] border border-[#D4AF37]/20 rounded-xl px-4 py-3 text-base text-starlight placeholder:text-[#5f6b7d] focus:outline-none focus:border-[#D4AF37]/60 transition-colors";

// Shown after Sign in with Apple when we don't yet know the member's birth details.
export default function Welcome() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ birth_date: "", birth_time: "", birth_place: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (loading) return null;
  if (!user) return <Navigate to="/" replace />;
  if (user.trinity) return <Navigate to="/today" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.birth_date) return setError("Please choose your date of birth");
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/profile/birth", {
        ...form,
        birth_time: form.birth_time || null,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      setUser(data);
      navigate("/today");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  return (
    <div className="app-shell text-starlight relative">
      <CelestialBackground />
      <div className="relative z-10 px-6 pb-16" data-testid="welcome-page">
        <header className="safe-top box-content h-16 flex items-center gap-2">
          <SiteMenu />
          <img src="/astra-mark-128.png" alt="" aria-hidden="true" className="w-9 h-9" />
          <span className="font-display text-lg tracking-[0.3em]">ASTRA</span>
        </header>
        <p className="text-[11px] uppercase tracking-[0.3em] gold-text mb-1">One last step</p>
        <h1 className="text-3xl font-light mb-2">Welcome{user.name && user.name !== "Stargazer" ? `, ${user.name.split(" ")[0]}` : ""}</h1>
        <p className="text-sm text-[#A7B0C0] mb-5">Tell us when you were born so Astra can read your Sun, Moon and Rising signs.</p>
        <form onSubmit={submit} className="space-y-3" data-testid="welcome-form">
          <div>
            <label className="text-xs text-[#A7B0C0] ml-1 block mb-1">Date of birth</label>
            <BirthDatePicker testId="welcome-dob" value={form.birth_date} onChange={(v) => setForm({ ...form, birth_date: v })} />
          </div>
          <div>
            <label className="text-xs text-[#A7B0C0] ml-1 block mb-1">Time of birth (optional, for Rising sign)</label>
            <BirthTimePicker testId="welcome-tob" value={form.birth_time} onChange={(v) => setForm({ ...form, birth_time: v })} />
          </div>
          <input data-testid="welcome-pob" className={inputCls} placeholder="City of birth (optional)"
            value={form.birth_place} onChange={(e) => setForm({ ...form, birth_place: e.target.value })} />
          {error && <p data-testid="welcome-error" className="text-sm text-red-300">{error}</p>}
          <button data-testid="btn-welcome-submit" type="submit" disabled={busy}
            className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium text-lg py-3.5 rounded-full transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={20} /> : "Reveal my reading"}
          </button>
        </form>
      </div>
    </div>
  );
}
